import {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

interface UseHorizontalCarouselOptions {
  pageCount: number;
  enabled?: boolean;
  dragMode?: 'momentum' | 'page-snap';
  itemsPerPage?: number;
  itemCount?: number;
}

interface HorizontalCarouselHandlers {
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onClickCapture: (event: ReactMouseEvent<HTMLDivElement>) => void;
}

interface UseHorizontalCarouselResult {
  viewportRef: RefObject<HTMLDivElement>;
  activePage: number;
  isDragging: boolean;
  scrollToPage: (page: number) => void;
  handlers: HorizontalCarouselHandlers;
}

type VelocitySample = {
  x: number;
  time: number;
};

const DRAG_THRESHOLD = 4;
const VELOCITY_WINDOW_MS = 100;
const MIN_MOMENTUM_VELOCITY = 0.04;
const STOP_VELOCITY = 0.015;
const FRICTION_PER_16MS = 0.94;
const MAX_VELOCITY = 2.5;

export const useHorizontalCarousel = ({
  pageCount,
  enabled = true,
  dragMode = 'momentum',
  itemsPerPage = 1,
  itemCount,
}: UseHorizontalCarouselOptions): UseHorizontalCarouselResult => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  const didDragRef = useRef(false);
  const suppressClickRef = useRef(false);
  const dragStartPageRef = useRef(0);
  const snapTargetPageRef = useRef<number | null>(null);
  const snapFrameRef = useRef<number | null>(null);
  const velocitySamplesRef = useRef<VelocitySample[]>([]);
  const momentumFrameRef = useRef<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activePage, setActivePage] = useState(0);

  const stopMomentum = () => {
    if (momentumFrameRef.current !== null) {
      cancelAnimationFrame(momentumFrameRef.current);
      momentumFrameRef.current = null;
    }
  };

  useEffect(
    () => () => {
      stopMomentum();
      if (snapFrameRef.current !== null) {
        cancelAnimationFrame(snapFrameRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport || !enabled || pageCount <= 1) {
      setActivePage(0);
      return;
    }

    const updateActivePage = () => {
      if (
        dragMode === 'page-snap' &&
        (didDragRef.current || snapTargetPageRef.current !== null)
      ) {
        return;
      }

      const maxScrollLeft = Math.max(
        0,
        viewport.scrollWidth - viewport.clientWidth,
      );

      if (maxScrollLeft === 0) {
        setActivePage(0);
        return;
      }

      if (dragMode === 'momentum') {
        const page = Math.round(
          (viewport.scrollLeft / maxScrollLeft) * (pageCount - 1),
        );
        setActivePage(page);
        return;
      }

      let page = 0;
      let smallestDistance = Number.POSITIVE_INFINITY;

      for (let index = 0; index < pageCount; index += 1) {
        const distance = Math.abs(
          viewport.scrollLeft - getPageScrollLeft(index),
        );
        if (distance < smallestDistance) {
          smallestDistance = distance;
          page = index;
        }
      }

      setActivePage(page);
    };

    updateActivePage();

    const resizeObserver = new ResizeObserver(updateActivePage);
    resizeObserver.observe(viewport);
    viewport.addEventListener('scroll', updateActivePage, { passive: true });

    return () => {
      resizeObserver.disconnect();
      viewport.removeEventListener('scroll', updateActivePage);
    };
  }, [enabled, pageCount, dragMode, itemsPerPage, itemCount]);

  const getPageScrollLeft = (page: number) => {
    const viewport = viewportRef.current;
    if (!viewport) return 0;

    const maxScrollLeft = Math.max(
      0,
      viewport.scrollWidth - viewport.clientWidth,
    );

    if (dragMode === 'momentum') {
      return pageCount <= 1 ? 0 : (maxScrollLeft * page) / (pageCount - 1);
    }

    const children = Array.from(viewport.children) as HTMLElement[];
    const firstItem = children[0];

    const realItemCount = itemCount ?? children.length;
    const maxStartIndex = Math.max(0, realItemCount - itemsPerPage);
    const requestedStartIndex = page * itemsPerPage;
    const targetIndex = Math.min(requestedStartIndex, maxStartIndex);
    const targetItem = children[targetIndex];

    if (firstItem && targetItem) {
      const itemLeft = targetItem.offsetLeft - firstItem.offsetLeft;
      return Math.min(maxScrollLeft, Math.max(0, itemLeft));
    }

    return pageCount <= 1 ? 0 : (maxScrollLeft * page) / (pageCount - 1);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!enabled || event.pointerType !== 'mouse' || event.button !== 0) return;

    const viewport = viewportRef.current;
    if (!viewport || viewport.scrollWidth <= viewport.clientWidth) return;

    stopMomentum();
    if (snapFrameRef.current !== null) {
      cancelAnimationFrame(snapFrameRef.current);
      snapFrameRef.current = null;
    }
    snapTargetPageRef.current = null;
    setIsDragging(false);

    dragStartPageRef.current = activePage;
    dragStartXRef.current = event.clientX;
    dragStartScrollLeftRef.current = viewport.scrollLeft;
    didDragRef.current = false;
    velocitySamplesRef.current = [
      { x: event.clientX, time: performance.now() },
    ];
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!enabled || event.pointerType !== 'mouse' || event.buttons !== 1)
      return;

    const viewport = viewportRef.current;
    if (!viewport || viewport.scrollWidth <= viewport.clientWidth) return;

    const deltaX = event.clientX - dragStartXRef.current;

    if (!didDragRef.current && Math.abs(deltaX) <= DRAG_THRESHOLD) return;

    if (!didDragRef.current) {
      didDragRef.current = true;
      setIsDragging(true);
      viewport.setPointerCapture(event.pointerId);
    }

    viewport.scrollLeft = dragStartScrollLeftRef.current - deltaX;

    const now = performance.now();
    const samples = velocitySamplesRef.current;
    samples.push({ x: event.clientX, time: now });
    velocitySamplesRef.current = samples.filter(
      sample => now - sample.time <= VELOCITY_WINDOW_MS,
    );
  };

  const startMomentum = (initialVelocity: number) => {
    const viewport = viewportRef.current;
    if (!viewport || Math.abs(initialVelocity) < MIN_MOMENTUM_VELOCITY) {
      setIsDragging(false);
      return;
    }

    let velocity = Math.max(
      -MAX_VELOCITY,
      Math.min(MAX_VELOCITY, initialVelocity),
    );
    let previousTime = performance.now();

    const animate = (now: number) => {
      const deltaTime = Math.min(now - previousTime, 32);
      previousTime = now;

      const previousScrollLeft = viewport.scrollLeft;
      viewport.scrollLeft += velocity * deltaTime;

      const hitEdge = viewport.scrollLeft === previousScrollLeft;
      velocity *= Math.pow(FRICTION_PER_16MS, deltaTime / 16);

      if (hitEdge || Math.abs(velocity) < STOP_VELOCITY) {
        momentumFrameRef.current = null;
        setIsDragging(false);
        return;
      }

      momentumFrameRef.current = requestAnimationFrame(animate);
    };

    momentumFrameRef.current = requestAnimationFrame(animate);
  };

  const getDragVelocity = (samples: VelocitySample[]) => {
    const first = samples[0];
    const last = samples.at(-1);

    if (!first || !last) return 0;

    const elapsed = last.time - first.time;
    return elapsed > 0 ? -(last.x - first.x) / elapsed : 0;
  };

  const finishPageSnap = (
    event: ReactPointerEvent<HTMLDivElement>,
    viewport: HTMLDivElement,
    recentSamples: VelocitySample[],
  ) => {
    const velocity = getDragVelocity(recentSamples);
    const distance = dragStartXRef.current - event.clientX;
    const direction = Math.sign(distance || velocity);
    const shouldChangePage =
      Math.abs(distance) >= viewport.clientWidth * 0.08 ||
      Math.abs(velocity) >= 0.25;
    const targetPage = shouldChangePage
      ? dragStartPageRef.current + direction
      : dragStartPageRef.current;
    const nextPage = Math.max(0, Math.min(pageCount - 1, targetPage));

    didDragRef.current = false;
    suppressClickRef.current = true;
    scrollToPage(nextPage);
  };

  const finishMomentum = (recentSamples: VelocitySample[], now: number) => {
    const last = recentSamples.at(-1);

    if (recentSamples.length < 2 || !last) {
      setIsDragging(false);
      return;
    }

    const velocity = getDragVelocity(recentSamples);

    if (!velocity || now - last.time > VELOCITY_WINDOW_MS) {
      setIsDragging(false);
      return;
    }

    startMomentum(velocity);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse') return;

    const viewport = viewportRef.current;
    if (!viewport) {
      setIsDragging(false);
      return;
    }

    if (viewport.hasPointerCapture(event.pointerId)) {
      viewport.releasePointerCapture(event.pointerId);
    }

    if (!didDragRef.current) {
      setIsDragging(false);
      return;
    }

    const now = performance.now();
    const recentSamples = velocitySamplesRef.current.filter(
      sample => now - sample.time <= VELOCITY_WINDOW_MS,
    );

    if (dragMode === 'page-snap') {
      finishPageSnap(event, viewport, recentSamples);
      return;
    }

    finishMomentum(recentSamples, now);
  };

  const handleClickCapture = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!didDragRef.current && !suppressClickRef.current) return;

    event.preventDefault();
    event.stopPropagation();
    didDragRef.current = false;
    suppressClickRef.current = false;
  };

  const scrollToPage = (page: number) => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    stopMomentum();
    setIsDragging(false);

    const targetPage = Math.max(0, Math.min(pageCount - 1, page));

    if (dragMode !== 'page-snap') {
      const left = getPageScrollLeft(targetPage);
      viewport.scrollTo({ left, behavior: 'smooth' });
      return;
    }

    snapTargetPageRef.current = targetPage;
    setActivePage(targetPage);

    if (snapFrameRef.current !== null) {
      cancelAnimationFrame(snapFrameRef.current);
    }

    snapFrameRef.current = requestAnimationFrame(() => {
      const currentViewport = viewportRef.current;
      snapFrameRef.current = null;

      if (!currentViewport) {
        snapTargetPageRef.current = null;
        return;
      }

      const left = getPageScrollLeft(targetPage);

      currentViewport.scrollTo({ left, behavior: 'smooth' });

      const finishSnap = () => {
        const viewportNow = viewportRef.current;
        if (!viewportNow) {
          snapTargetPageRef.current = null;
          return;
        }

        if (Math.abs(viewportNow.scrollLeft - left) <= 1) {
          snapTargetPageRef.current = null;
          viewportNow.removeEventListener('scrollend', finishSnap);
        }
      };

      currentViewport.addEventListener('scrollend', finishSnap, { once: true });
      window.setTimeout(() => {
        snapTargetPageRef.current = null;
      }, 500);
    });
  };

  return {
    viewportRef,
    activePage,
    isDragging,
    scrollToPage,
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerUp,
      onClickCapture: handleClickCapture,
    },
  };
};
